import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../design-system';

// Body portal keeps guide text outside the reader's highlight-offset DOM.
export default function Spotlight({ selectors, soft = false, step, onPause }) {
  const { t } = useTranslation('common');
  const coachRef = useRef(null);
  const [coachPosition, setCoachPosition] = useState(null);
  const [rect, setRect] = useState(null);
  useEffect(() => {
    let frame;
    let watchedTarget;
    const resize = new ResizeObserver(() => update());
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const targets = selectors.flatMap(selector => [...document.querySelectorAll(selector)]);
        const target = targets.find(el => { const r = el.getBoundingClientRect(); return r.width && r.height && r.bottom > 0 && r.top < window.innerHeight; });
        if (target !== watchedTarget) {
          if (watchedTarget) resize.unobserve(watchedTarget);
          if (target) resize.observe(target);
          watchedTarget = target;
        }
        const r = target?.getBoundingClientRect();
        const coach = coachRef.current?.getBoundingClientRect();
        if (r && coach) {
          const w = window.innerWidth;
          const h = window.innerHeight;
          const padding = 16;
          const positions = [
            { left: r.right + padding, top: r.top },
            { left: r.left - coach.width - padding, top: r.top },
            { left: r.left, top: r.bottom + padding },
            { left: r.left, top: r.top - coach.height - padding },
          ];
          const position = positions.find(p => p.left >= padding && p.top >= padding && p.left + coach.width <= w - padding && p.top + coach.height <= h - 100);
          setCoachPosition(position || null);
        } else setCoachPosition(null);
        setRect(r ? { left: Math.max(0, r.left - 6), top: Math.max(0, r.top - 6), right: Math.min(window.innerWidth, r.right + 6), bottom: Math.min(window.innerHeight, r.bottom + 6) } : null);
      });
    };
    const observer = new MutationObserver(update);
    observer.observe(document.querySelector('.app-container') || document.body, { subtree: true, childList: true });
    window.addEventListener('resize', update);
    document.addEventListener('scroll', update, true);
    const escape = e => { if (e.key === 'Escape') onPause(); };
    document.addEventListener('keydown', escape);
    if (coachRef.current) resize.observe(coachRef.current);
    update();
    return () => { cancelAnimationFrame(frame); observer.disconnect(); resize.disconnect(); window.removeEventListener('resize', update); document.removeEventListener('scroll', update, true); document.removeEventListener('keydown', escape); };
  }, [selectors, onPause]);
  const mask = rect && !soft;
  return createPortal(<div className="guided-tour" data-reader-overlay="true">
    {mask && <><div className="guided-tour__shade" style={{ top: 0, height: rect.top, left: 0, right: 0 }} />
      <div className="guided-tour__shade" style={{ top: rect.bottom, bottom: 0, left: 0, right: 0 }} />
      <div className="guided-tour__shade" style={{ top: rect.top, height: rect.bottom - rect.top, left: 0, width: rect.left }} />
      <div className="guided-tour__shade" style={{ top: rect.top, height: rect.bottom - rect.top, left: rect.right, right: 0 }} />
      </>}
    <aside ref={coachRef} style={coachPosition ? { ...coachPosition, right: 'auto', bottom: 'auto' } : undefined} className="guided-tour__coach" aria-label={t('guided.title')} aria-live="polite">
      <small>{t(`guided.${step}.count`)}</small><h3>{t(`guided.${step}.title`)}</h3><p>{t(`guided.${step}.body`)}</p>
      {!rect && <p>{t('guided.waiting')}</p>}
      <Button size="sm" onClick={onPause}>{t('guided.pause')}</Button>
    </aside>
  </div>, document.body);
}
